<script setup lang="ts" generic="T extends string">
// The detail panel's tab bar (detailtabs.ts), and a drawer's (Node software). A dot on a tab not shown
// says something in it wants a look (an error event not yet seen, a
// caster's bad line). Arrow keys move between tabs, as ARIA's tablist.
import { useTemplateRef } from 'vue'

const props = defineProps<{
    tabs: { id: T; label: string }[]
    modelValue: string
    dots?: Partial<Record<T, 'bad' | 'warn'>>
}>()
const emit = defineEmits<{ 'update:modelValue': [T] }>()
const bar = useTemplateRef<HTMLDivElement>('bar')

function key(e: KeyboardEvent) {
    const i = props.tabs.findIndex((x) => x.id === props.modelValue)
    const n = props.tabs.length
    const to = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n : -1
    if (to < 0) return
    e.preventDefault()
    emit('update:modelValue', props.tabs[to]!.id)
    bar.value?.querySelectorAll<HTMLButtonElement>('[role=tab]')[to]?.focus()
}
</script>

<template>
    <div
        ref="bar"
        role="tablist"
        class="sticky top-0 z-10 flex gap-0.5 border-b border-line bg-bg-1 px-2"
        data-test="detail-tabs"
        @keydown="key"
    >
        <button
            v-for="t in tabs"
            :key="t.id"
            role="tab"
            :aria-selected="t.id === modelValue"
            :tabindex="t.id === modelValue ? 0 : -1"
            class="relative -mb-px rounded-none border-0 border-b-2 bg-transparent px-2.5 py-2 text-xs font-medium hover:bg-transparent hover:text-fg"
            :class="t.id === modelValue ? 'border-accent text-fg' : 'border-transparent text-fg-2'"
            :data-test="`tab-${t.id}`"
            @click="emit('update:modelValue', t.id)"
        >
            {{ t.label
            }}<span
                v-if="dots?.[t.id] && t.id !== modelValue"
                class="absolute top-1 right-0.5 h-1.5 w-1.5 rounded-full"
                :class="dots[t.id] === 'bad' ? 'bg-bad' : 'bg-warn'"
                :aria-label="dots[t.id] === 'bad' ? 'has a problem' : 'has a warning'"
            />
        </button>
    </div>
</template>
